import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../ui/Kit';
import AiCallsTab from './AiCallsTab';
import CallLogTable, { NoCallsEmptyState } from './CallLogTable';
import { matchAiMirrorCallLogs } from '../../lib/leadActivityFeed';

const { FiZap } = FiIcons;

export const CALLS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'manual', label: 'Manual' },
  { id: 'ai', label: 'AI' },
];

function SectionHeading({ children }) {
  return (
    <p className="px-4 pt-4 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
      {children}
    </p>
  );
}

/**
 * Manual = call_logs, AI = thoughtly_calls. call_logs rows that thoughtly-webhook
 * wrote for an AI call are left out of Manual since the AI list already shows them.
 */
export default function CallsTab({
  filter = 'all',
  onFilterChange,
  callLogs = [],
  thoughtlyCalls = [],
  thoughtlyAgents = [],
  thoughtlyLoading = false,
  hasPhone,
  onCallLead,
  onSendToAiCaller,
}) {
  const manualCalls = useMemo(() => {
    const mirrors = matchAiMirrorCallLogs(callLogs, thoughtlyCalls);
    return mirrors.size ? callLogs.filter((row) => !mirrors.has(row.id)) : callLogs;
  }, [callLogs, thoughtlyCalls]);

  const counts = { all: manualCalls.length + thoughtlyCalls.length, manual: manualCalls.length, ai: thoughtlyCalls.length };
  const aiTab = (
    <AiCallsTab
      calls={thoughtlyCalls}
      agents={thoughtlyAgents}
      loading={thoughtlyLoading}
      hasPhone={hasPhone}
      onSendToAiCaller={onSendToAiCaller}
    />
  );

  let content;
  if (filter === 'manual') {
    content = <CallLogTable callLogs={manualCalls} hasPhone={hasPhone} onCallLead={onCallLead} />;
  } else if (filter === 'ai') {
    content = <div className="p-4">{aiTab}</div>;
  } else if (!manualCalls.length && !thoughtlyCalls.length && !thoughtlyLoading) {
    content = (
      <NoCallsEmptyState
        title="No calls yet"
        message="Manual and AI calls to this lead will appear here once they are recorded."
        hasPhone={hasPhone}
        onCallLead={onCallLead}
      >
        {hasPhone && onSendToAiCaller && (
          <Button size="sm" variant="outline" onClick={onSendToAiCaller}>
            <SafeIcon icon={FiZap} className="mr-2" />
            Send to AI Caller
          </Button>
        )}
      </NoCallsEmptyState>
    );
  } else {
    content = (
      <>
        {manualCalls.length > 0 && (
          <section>
            <SectionHeading>Manual calls ({manualCalls.length})</SectionHeading>
            <CallLogTable callLogs={manualCalls} hasPhone={hasPhone} onCallLead={onCallLead} />
          </section>
        )}
        {(thoughtlyCalls.length > 0 || thoughtlyLoading) && (
          <section>
            <SectionHeading>AI calls ({thoughtlyCalls.length})</SectionHeading>
            <div className="px-4 pb-4">{aiTab}</div>
          </section>
        )}
      </>
    );
  }

  return (
    <div className="-m-6 flex flex-col min-h-full">
      <div className="flex items-center gap-1 px-4 py-2.5 bg-white border-b border-gray-200" role="tablist" aria-label="Call type">
        {CALLS_FILTERS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={filter === id}
            onClick={() => onFilterChange?.(id)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === id
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
            <span className={`ml-1.5 ${filter === id ? 'text-white/80' : 'text-gray-400'}`}>{counts[id]}</span>
          </button>
        ))}
      </div>
      <div className="flex-1 flex flex-col">{content}</div>
    </div>
  );
}
